
import { Component, Ref, Prop, Vue, Watch } from 'vue-property-decorator'
import InputTextBase from '@/components/atoms/InputTextBase.vue'
import ImageBase from '@/components/atoms/ImageBase.vue'
import ModalWindow from '@/components/molecules/ModalWindow.vue'
import AnswerFormula from '@/components/molecules/AnswerFormula.vue'
import ButtonSubmitSquare from '@/components/molecules/ButtonSubmitSquare.vue'
import ButtonOptionalSquare from '@/components/molecules/ButtonOptionalSquare.vue'
import ButtonStrongSquare from '@/components/molecules/ButtonStrongSquare.vue'
import DrawingArea from '@/components/molecules/DrawingArea.vue'
import InputAnswerText from '@/components/molecules/InputAnswerText.vue'
import TogglePenControl from '@/components/organisms/study/TogglePenControl.vue'
import { PenSetting } from '@/const/PenSetting'
import { DrawingModel } from '@/models/DrawingModel'
import { StudyModel } from '@/models/StudyModel'

/**
 * 手書き入力モーダル
 */
@Component({
  components: {
    InputTextBase,
    ImageBase,
    ModalWindow,
    AnswerFormula,
    ButtonSubmitSquare,
    ButtonOptionalSquare,
    ButtonStrongSquare,
    DrawingArea,
    InputAnswerText,
    TogglePenControl,
  },
})
export default class ModalInputDrawing extends Vue {
  /** 入力値をlatex文字列として扱うか否か(デフォルトはlatex、平文字列の場合のみfalseを設定) */
  @Prop({ default: true })
  private isLatex!: boolean

  /**
   * モーダルをドラッグ可能にするか否か
   *   デフォルトはOFF=位置固定
   *   trueの場合はタイトル部を掴めるようにする
   */
  @Prop({ default: false })
  draggable!: boolean

  /** モーダルウィンドウ */
  @Ref()
  modalWindow!: ModalWindow

  /** 手書きエリア */
  @Ref()
  drawingArea!: DrawingArea

  /** ペン選択 */
  @Ref()
  togglePenControl!: TogglePenControl

  /** タイトル文字列 */
  private title = ''

  /** 入力値 */
  private inputValue = ''

  /** 入力タイプ */
  private inputType = ''

  //=== 手書き情報

  /** ペン情報 */
  private activePen = PenSetting.PEN_BLACK

  /** ストローク情報 */
  public strokes: DrawingModel.Stroke[] = []

  /**
   * 入力モードをテンプレート層で使用するためのラッパー
   *
   * @returns 入力モード定義
   */
  private get drawingInputMode(): object {
    return StudyModel.DRAWING_INPUT_MODE
  }

  /**
   * モーダルのドラッグ対象
   *
   * @returns ドラッグ不可の場合false、可の場合CSSセレクタ
   */
  private get draggableTarget(): boolean | string {
    // ドラッグ不可の場合はfalseを固定で返す
    if (!this.draggable) {
      return false
    }

    // ドラッグ可の場合はヘッダ部のCSSセレクタを返す
    return '.modal-input-drawing__main__header'
  }

  /**
   * ストローク更新時の通知処理
   */
  @Watch('strokes')
  private updateStroke(): void {
    this.$emit('updateStroke')
  }

  /**
   * モーダルの消去処理
   */
  public hide(): void {
    this.modalWindow.hide()
  }

  /**
   * モーダルの表示処理
   *
   * @param title タイトル文字列
   * @param inputType 入力モード
   * @param defaultValue 初期値
   * @param defaultStrokes 入力済みのストローク情報
   */
  public show(title: string, inputType: string, defaultValue?: string, defaultStrokes?: DrawingModel.Stroke[]): void {
    // タイトル設定
    this.title = title

    // 入力値の引き継ぎ
    this.inputValue = defaultValue || ''
    this.strokes.splice(0)
    if (defaultStrokes) {
      this.strokes.push(...defaultStrokes)
    }

    // ペンの初期化
    this.activePen = PenSetting.PEN_BLACK

    // 入力タイプの引き継ぎ
    this.inputType = inputType

    this.modalWindow.show()
  }

  /**
   * 手書きモード時の入力値(手書き入力を解析した結果)を更新する
   *
   * @param value 入力値
   */
  public updateDrawingValue(value: string): void {
    // 手書きモード以外の場合は終了(モード切り替えがあった場合の対処)
    if (this.inputType !== StudyModel.DRAWING_INPUT_MODE.PEN) {
      return
    }

    this.inputValue = value
  }

  /**
   * ペンの切り替え
   *
   * @param activePen アクティブなペン
   */
  private togglePen(activePen: DrawingModel.PenAsset): void {
    this.activePen = activePen
  }

  /**
   * ストロークの消去
   */
  private clearStrokes(): void {
    this.strokes.splice(0)
    // キャンバスの再描画
    this.drawingArea.drawAllStrokes()
  }

  /**
   * 入力モードをキーボードに切り替え
   */
  private toggleKeyboardMode(): void {
    this.inputValue = ''
    this.strokes.splice(0)
    this.inputType = StudyModel.DRAWING_INPUT_MODE.KEYBOARD
  }

  /**
   * 入力モードを手書きに切り替え
   */
  private togglePenMode(): void {
    this.inputValue = ''
    // ペンの初期化
    this.activePen = PenSetting.PEN_BLACK
    this.inputType = StudyModel.DRAWING_INPUT_MODE.PEN
  }

  /**
   * 入力値の確定(送信)処理
   */
  private submit(): void {
    // 親コンポーネントにイベントを通知し、入力値を受け渡す
    this.$emit('submit', this.inputValue, this.inputType, this.strokes)

    this.hide()
  }

  /**
   * モーダル消去時のイベントを通知
   *   使用時に@closed='function'と設定すればモーダル消去時に処理の実行が可能
   */
  private closed(): void {
    this.$emit('closed')
  }
}
